import {
  Outlet,
  createFileRoute,
  notFound,
  redirect,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DEFAULT_LANGUAGE,
  isLiveLanguage,
  isSupportedLanguage,
} from '@/shared/i18n/supported-languages'
import { replaceSupportedLanguageInHref } from '@/shared/i18n/localized-path'
import { useLanguage } from '@/shared/i18n/use-language'

export const Route = createFileRoute('/$lang')({
  beforeLoad: ({ params, location }) => {
    if (!isSupportedLanguage(params.lang)) {
      throw notFound()
    }
    // Also normalize old English URLs in the local dev server, which has no
    // Caddy redirect in front of the app.
    if (
      params.lang === DEFAULT_LANGUAGE &&
      typeof window !== 'undefined' &&
      /^\/en(?:\/|$)/.test(window.location.pathname)
    ) {
      throw redirect({ href: location.publicHref, replace: true })
    }
    // A hidden language (see LIVE_LANGUAGES) opens the same page in English.
    if (!isLiveLanguage(params.lang)) {
      throw redirect({
        href: replaceSupportedLanguageInHref(location.href, DEFAULT_LANGUAGE),
        replace: true,
      })
    }
  },
  component: LangLayout,
  staticData: { layout: 'public' },
})

function LangLayout() {
  const { lang } = Route.useParams()
  const { i18n } = useTranslation()
  const { setLanguage, language } = useLanguage()

  useEffect(() => {
    if (!isSupportedLanguage(lang)) return
    document.documentElement.lang = lang
    if (i18n.language !== lang) {
      void i18n.changeLanguage(lang)
    }
    if (language !== lang) {
      setLanguage(lang)
    }
  }, [lang, i18n, language, setLanguage])

  return <Outlet />
}
